import { useCallback, useEffect, useRef, useState } from 'react'
import { getUserMetrics } from '../services/MetricsDashboardService'
import { PagedUserMetrics, UserMetrics } from '../types/metrics'

const DEFAULT_PAGE_SIZE = 25
const SEARCH_DEBOUNCE_MS = 300

type UseUserMetricsQuery = {
  rows: UserMetrics[]
  totalElements: number
  loading: boolean
  error: string | null
  page: number
  pageSize: number
  search: string
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
  onSearchChange: (value: string) => void
}

const useUserMetricsQuery = (refreshKey: number): UseUserMetricsQuery => {
  const [data, setData] = useState<PagedUserMetrics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState<{ page: number; search: string }>({ page: 0, search: '' })
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    setLoading(true)
    setError(null)
    getUserMetrics(query.page, pageSize, query.search)
      .then(setData)
      .catch(() => setError('Could not load reviewer data.'))
      .finally(() => setLoading(false))
  }, [query, pageSize, refreshKey])

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const onSearchChange = useCallback((value: string) => {
    setSearch(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setQuery({ page: 0, search: value })
    }, SEARCH_DEBOUNCE_MS)
  }, [])

  const onPageChange = useCallback((page: number) => {
    setQuery(q => ({ ...q, page }))
  }, [])

  const onPageSizeChange = useCallback((size: number) => {
    setPageSize(size)
    setQuery(q => ({ ...q, page: 0 }))
  }, [])

  return {
    rows: data?.content ?? [],
    totalElements: data?.totalElements ?? 0,
    loading,
    error,
    page: query.page,
    pageSize,
    search,
    onPageChange,
    onPageSizeChange,
    onSearchChange,
  }
}

export default useUserMetricsQuery
